export type ComplianceResponseType = 'aggregate' | 'yes_or_no' | 'yes_or_no_or_na';
export type YesNoOption = 'yes' | 'no' | undefined;
export type YesNoNaOption = YesNoOption | 'n/a';

export const COMPLIANCE_FILTERS_KEY = 'compliance_filters';

export const COMPLIANCE_CHECKLIST_STATUS_COMPLETE = 'Complete';
export const COMPLIANCE_CHECKLIST_STATUS_NOT_STARTED = 'Not Started';
export const COMPLIANCE_CHECKLIST_STATUS_ACTION_REQUIRED = 'Action Required';
export const COMPLIANCE_CHECKLIST_STATUS_RE_REVIEW_REQUIRED = 'Re-review Required';
export const COMPLIANCE_CHECKLIST_STATUS_ADVISOR_ACTION_REQUIRED = 'Advisor Action Required';
export const COMPLIANCE_CHECKLIST_STATUS_RE_REVIEW_PENDING = 'Re-Review Pending';
export const COMPLIANCE_CHECKLIST_STATUSES = [
  COMPLIANCE_CHECKLIST_STATUS_COMPLETE,
  COMPLIANCE_CHECKLIST_STATUS_NOT_STARTED,
  COMPLIANCE_CHECKLIST_STATUS_ACTION_REQUIRED,
  COMPLIANCE_CHECKLIST_STATUS_RE_REVIEW_REQUIRED,
  COMPLIANCE_CHECKLIST_STATUS_ADVISOR_ACTION_REQUIRED,
  COMPLIANCE_CHECKLIST_STATUS_RE_REVIEW_PENDING,
] as const;
export type ComplianceChecklistStatus = typeof COMPLIANCE_CHECKLIST_STATUSES[number];

export const COMPLIANCE_CHECKLIST_STATUSES_LIMITED = [
  COMPLIANCE_CHECKLIST_STATUS_COMPLETE,
  COMPLIANCE_CHECKLIST_STATUS_NOT_STARTED,
  COMPLIANCE_CHECKLIST_STATUS_ACTION_REQUIRED,
  COMPLIANCE_CHECKLIST_STATUS_RE_REVIEW_REQUIRED,
] as const;
export type ComplianceChecklistStatusLimited = typeof COMPLIANCE_CHECKLIST_STATUSES_LIMITED[number];

export const COMPLIANCE_CHECKLIST_ACTOR_ADMIN_ADVISOR = 'Admin / Advisor';
export const COMPLIANCE_CHECKLIST_ACTOR_PM = 'PM';
export const COMPLIANCE_CHECKLIST_ACTOR_COMPLIANCE = 'Compliance';
export const COMPLIANCE_CHECKLIST_ACTOR = [
  COMPLIANCE_CHECKLIST_ACTOR_ADMIN_ADVISOR,
  COMPLIANCE_CHECKLIST_ACTOR_PM,
  COMPLIANCE_CHECKLIST_ACTOR_COMPLIANCE,
] as const;
export type ComplianceChecklistActor = typeof COMPLIANCE_CHECKLIST_ACTOR[number];

export const COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD = 'Household';
export const COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL = 'Individual';
export const COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE = 'Corporate';

export const COMPLIANCE_CLIENT_RTQ_LEVELS = [
  COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD,
  COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL,
  COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE,
] as const;
export type ComplianceClientRTQLevel = typeof COMPLIANCE_CLIENT_RTQ_LEVELS[number];

export type ComplianceNotificationType = 'ComplianceChecklistActionRequired' | 'ComplianceChecklistReviewRequired';

export type ComplianceChecklistWithNotes = {
  checklist: ComplianceQuestion[];
  notes: ComplianceNotesMap;
};

export type ComplianceQuestion = {
  uuid: string;
  id: number;
  shortCode: ShortCode;
  text: string;
  subText: string;
  parentId: number | null;
  type: string;
  children?: ComplianceQuestion[];
  childrenStatus?: boolean;
  value?: string | null;
};

export type ComplianceNote = {
  content: string;
  noteType: string; //'Compliance'
  owners: string[]; // List of the contact / legal entity Qids
  title: string;
  itemId: number; // The heading's ID (e.g. Documents)
  endTime?: string;
  lastUpdated?: string;
  lastUpdatedBy?: string;
  startTime?: string;
  uuid?: string;
  author?: string;
  contentUpdateTime?: string;
};

export type ComplianceNotificationPayload = {
  changeBy: string;
  notificationType: ComplianceNotificationType;
  partnerID: number;
  householdId: string;
  entityId?: string;
  individualId?: string;
};

export interface ComplianceApprovalNote {
  complianceApprovalDate: string | null;
  approvedBy: string | null;
}

export type ContactChecklist = {
  contactQID: string;
  contactName?: string;
  checklistStatus?: ComplianceChecklistStatus;
  checklist: ComplianceQuestion[];
  notes: ComplianceNotesMap;
};

export type ChecklistSubmission = {
  values: Record<string, string | null | undefined>;
  notes: ComplianceNote[];
  assignee?: string | null;
};

export type ComplianceNotesMap = Record<number, ComplianceNote>;

export type SectionQuestion = {
  aId: string;
  text: string;
  type: ComplianceResponseType;
  value: YesNoNaOption;
  taskToDisplay: ShortCode;
  children?: SectionQuestion[];
};

export type Section = {
  text: 'Documents' | 'KYC Information' | 'Regulatory' | 'PM Notes' | 'IPS Check';
  type: 'aggregate';
  value: YesNoOption;
  children: SectionQuestion[];
};

export type ShortCode =
  | 'QVault'
  | 'SelectDocument'
  | 'Messages'
  | 'PDFViewer'
  | 'documents'
  | 'welcome_letter'
  | 'referral_disclosure'
  | 'all_ima_agreement_sigs'
  | 'all_ima_fee_sigs'
  | 'partner_office'
  | 'household_set_up'
  | 'rep_code_match'
  | 'kyc_info'
  | 'onboarding'
  | 'valid_id'
  | 'enhanced_kyc'
  | 'employer'
  | 'incomes'
  | 'assets_liabilities'
  | 'regulatory'
  | 'insider_status'
  | 'domestic_foreign_pep'
  | 'third_party'
  | 'other_regulatory'
  | 'pm_notes'
  | 'rtq_notes'
  | 'aml_risk'
  | 'pm_mandate'
  | 'sophistication'
  | 'restrictions_unique_circumstances'
  | 'ips_check'
  | 'ips_completed'
  | 'ips_language'
  | 'investment_restriction'
  | 'ips_signed'
  | 'advising_rep_name'
  | 'all_fields_populated'
  | 'enhanced_kyc_notes'
  | 'Other'
  | 'All Contact Info'
  | 'rtq_liquidity_needs'
  | 'rtq_time_horizion'
  | 'adequate_risk_profile'
  | 'ips_liquidity_needs'
  | 'ips_time_horizion';

export const automatedShortCodes = [
  'valid_id',
  'enhanced_kyc',
  'employer',
  'insider_status',
  'domestic_foreign_pep',
  'third_party',
  'other_regulatory',
];

export interface ClientComplianceReview {
  QID: string;
  name: string;
  complianceChecklistStatus: ComplianceChecklistStatus;
  ipsAcceptanceDate: string | null;
  rtqLevel: ComplianceClientRTQLevel;
  partnerName: string;
  household: {
    QID: string;
    name: string;
    ipsAcceptanceDate?: string;
    partnerName?: string;
  };
  kycId: number;
  assignee: string | null;
  iaCode: string;
  responsibleComplianceActors: ComplianceChecklistActor[] | null;
}

export interface ClientComplianceReviewAssigneeUpdate {
  qid: string;
  assignee: string | null;
}

export interface ClientComplianceReviewFilters {
  partnerName: string | null;
  complianceChecklistStatus: ComplianceChecklistStatusLimited | null;
  ipsDateStart: string | undefined;
  ipsDateEnd: string | undefined;
  assignee: string | null;
  responsibleComplianceActors: ComplianceChecklistActor | null;
}

export interface ClientComplianceReviewQueryParams {
  partnerId?: number;
  iaCode?: string;
}
